import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO, { faqSchema } from '../../Components/SEO';
import SEOPageLayout from '../../Components/SEOPageLayout';

export default function Pricing() {
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    { question: "How does the pay-per-question model work?", answer: "Instead of a monthly subscription, you buy 'credits'. One credit equals one AI query. You can purchase credits in bulk directly through the app via UPI." },
    { question: "What is the minimum purchase amount?", answer: "The minimum purchase is 25 questions, which costs roughly ₹315 depending on the tier." },
    { question: "What is included in the Premium upgrade?", answer: "The ₹199 Premium upgrade is a one-time fee that unlocks the Auto-Typing engine (which physically types code solutions for you) and advanced process hiding features. It does not include question credits." },
    { question: "How does the Neural Driver pricing work?", answer: "Using the Neural Driver (local AI) does not consume cloud AI credits. Once you have the software, local inference is completely free as it uses your own hardware." },
    { question: "Do you offer refunds?", answer: "We offer a 7-day money-back guarantee for unused credits if you are not satisfied with the software." }
  ];

  return (
    <SEOPageLayout>
      <SEO
        title="CheckScript Pricing — Affordable AI Assessment Plans Starting ₹10/question"
        description="Flexible, pay-as-you-go pricing for CheckScript AI. Buy question credits starting at just ₹10. No monthly subscriptions, pay only for what you use via UPI."
        keywords="CheckScript pricing, AI exam tool cost, affordable AI study tool, CheckScript plans"
        path="/pricing"
        structuredData={faqSchema(faqs)}
      />

      <section className="seo-hero seo-animate">
        <div className="seo-mesh-bg"></div>
        <div className="seo-container seo-container--sm">
          <div className="seo-hero-badge">💳 Pay As You Go</div>
          <h1 className="seo-h1">
            Simple, Transparent <span className="seo-gradient-text">Pricing</span>
          </h1>
          <p className="seo-lead">
            No expensive monthly subscriptions. Pay only for the AI assistance you actually use. All payments are securely processed via UPI.
          </p>
        </div>
      </section>

      <section className="seo-section seo-animate">
        <div className="seo-container">
          <div className="seo-bento-grid">
            
            {/* Basic Card */}
            <div className="seo-pricing-card">
              <h3 className="seo-h3">Standard Plan</h3>
              <p className="seo-text-secondary seo-mb-24">Perfect for occasional study assistance and exam prep.</p>
              <div className="seo-pricing-amount">₹12.59<span className="seo-pricing-period">/question</span></div>
              <ul className="seo-pricing-features seo-mb-32">
                <li><span className="seo-check">✓</span> Advanced stealth capture system Screen Capture</li>
                <li><span className="seo-check">✓</span> Cloud AI Inference</li>
                <li><span className="seo-check">✓</span> Free Neural Driver (Local AI)</li>
                <li><span className="seo-check">✓</span> Sub-100ms Remote Desktop</li>
                <li className="seo-text-muted"><span className="seo-cross">✗</span> Auto-Typing Engine</li>
              </ul>
              <button className="seo-btn seo-btn--ghost seo-w-full">Min. 25 Questions</button>
            </div>

            {/* Premium Card */}
            <div className="seo-pricing-card seo-pricing-card--featured">
              <div className="seo-pricing-badge">Most Popular</div>
              <h3 className="seo-h3">Premium Unlock</h3>
              <p className="seo-text-secondary seo-mb-24">Unlock advanced productivity tools for a one-time fee.</p>
              <div className="seo-pricing-amount">+₹199<span className="seo-pricing-period"> one-time</span></div>
              <ul className="seo-pricing-features seo-mb-32">
                <li><span className="seo-check">✓</span> Everything in Standard</li>
                <li><span className="seo-check">✓</span> <strong>Auto-Typing Code Engine</strong></li>
                <li><span className="seo-check">✓</span> Advanced Process Hiding</li>
                <li><span className="seo-check">✓</span> Priority API Routing</li>
                <li><span className="seo-check">✓</span> Extended Capture Settings</li>
              </ul>
              <button className="seo-btn seo-btn--primary seo-w-full">Download & Upgrade</button>
            </div>

            {/* Enterprise / Bulk Card */}
            <div className="seo-pricing-card">
              <h3 className="seo-h3">Bulk / Partner</h3>
              <p className="seo-text-secondary seo-mb-24">For heavy users and resellers looking for maximum ROI.</p>
              <div className="seo-pricing-amount">₹10<span className="seo-pricing-period">/question</span></div>
              <ul className="seo-pricing-features seo-mb-32">
                <li><span className="seo-check">✓</span> Volume discount pricing</li>
                <li><span className="seo-check">✓</span> Vendor dashboard access</li>
                <li><span className="seo-check">✓</span> Generate license keys</li>
                <li><span className="seo-check">✓</span> Up to 200% resale ROI</li>
                <li><span className="seo-check">✓</span> Dedicated support</li>
              </ul>
              <Link to="/contact" className="seo-btn seo-btn--secondary seo-w-full seo-flex-center">Contact Sales</Link>
            </div>

          </div>
        </div>
      </section>

      <section className="seo-section seo-section--alt seo-animate">
        <div className="seo-container seo-container--sm">
          <div className="seo-glass-card seo-text-center">
            <h2 className="seo-h2">Seamless UPI Payments</h2>
            <p className="seo-text-secondary seo-mb-32">
              We've made purchasing credits incredibly easy. Just generate an invoice directly inside the CheckScript application and scan the QR code with your favorite UPI app.
            </p>
            <div className="seo-flex seo-flex-center seo-gap-24">
              <span className="seo-label">Google Pay</span>
              <span className="seo-label">PhonePe</span>
              <span className="seo-label">Paytm</span>
              <span className="seo-label">BHIM</span>
            </div>
          </div>
        </div>
      </section>

      <section className="seo-section seo-animate">
        <div className="seo-container seo-container--sm">
          <h2 className="seo-h2 seo-text-center seo-mb-48">Pricing FAQ</h2>
          <ul className="seo-faq-list">
            {faqs.map((faq, i) => (
              <li key={i} className={`seo-faq-item ${openFaq === i ? 'active' : ''}`}>
                <button className="seo-faq-question" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  {faq.question}
                  <span className="seo-faq-arrow">▼</span>
                </button>
                <div className="seo-faq-answer">
                  <div className="seo-faq-answer-inner">{faq.answer}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

    </SEOPageLayout>
  );
}
